// src/pages/Admin/HotelAffiliationManagementPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Row,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Timeline,
  Typography,
  Modal,
  Progress,
  Steps,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SafetyOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {  } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import {
  CardTitle,
  DetailList,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatStrip,
  StatusTag,
  TabBar,
  fmtDate as fmtDay,
  tablePagination,
  useFlatPage,
} from "../../components/ui";
import notify from "../../utils/notify";

// Tabs rendered with our standard tab bar; only the active tab's content is mounted
const TabsHost = ({ activeKey, onChange, items }) => {
  const current = items.find((i) => i.key === activeKey) || items[0];
  return (
    <>
      <SectionCard flush>
        <TabBar
          tabs={items.map(({ key, label, count }) => ({ key, label, count }))}
          value={current.key}
          onChange={onChange}
        />
      </SectionCard>
      <div style={{ marginTop: 16 }}>{current.children}</div>
    </>
  );
};

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ─── helpers ────────────────────────────────────────────────────────────────

const statusTagColor = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("approved") || s === "3") return "green";
  if (s.includes("rejected") || s === "4") return "red";
  if (s.includes("pending") || s === "1" || s === "2") return "gold";
  if (s.includes("suspend")) return "volcano";
  return "default";
};

const kycColor = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("approved") || s === "4") return "green";
  if (s.includes("rejected") || s === "5") return "red";
  if (s.includes("resubmission") || s === "6") return "orange";
  if (s.includes("submitted") || s === "2" || s.includes("review") || s === "3") return "blue";
  return "default";
};

const kycLabel = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("approved") || s === "4") return "KYC Approved";
  if (s.includes("rejected") || s === "5") return "KYC Rejected";
  if (s.includes("resubmission") || s === "6") return "Resubmission Required";
  if (s.includes("review") || s === "3") return "Under Review";
  if (s.includes("submitted") || s === "2") return "Submitted";
  if (s === "notstarted" || s === "1") return "Not Started";
  return String(status);
};

const fmt     = (utc) => (utc ? new Date(utc).toLocaleString() : "—");
const fmtDate = (utc) => (utc ? new Date(utc).toLocaleDateString() : "—");

// ─── component ──────────────────────────────────────────────────────────────

const HotelAffiliationManagementPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const queryClient  = useQueryClient();
  const navigate     = useNavigate();
  const hotelId      = authData?.hotelId;

  const [settingsForm, setSettingsForm] = useState({
    allowAgentAffiliation: false,
    autoApproveAgents:     false,
    defaultCommissionPercentage: null,
  });
  const [drawerOpen,          setDrawerOpen]          = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [reviewNotes,         setReviewNotes]         = useState("");
  const [commissionPercentage, setCommissionPercentage] = useState(null);
  const [disableModalOpen,    setDisableModalOpen]    = useState(false);
  const [clearAgentModalOpen, setClearAgentModalOpen] = useState(false);

  // ── Activation wizard state ──────────────────────────────────────────────
  // Steps: 0 = configure commission/settings, 1 = configure SLA policy (optional)
  const [activationStep,   setActivationStep]   = useState(0);
  const [activationSlaForm, setActivationSlaForm] = useState({
    defaultBaseCommissionRate:  10,
    defaultMinBookingsPerMonth: 5,
    defaultMaxCancellationRate: 20,
    reviewPeriodDays:           30,
    slaText:                    "",
  });
  const [skipSla, setSkipSla] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedApplicationId(null);
    setReviewNotes("");
    setCommissionPercentage(null);
  };

  // ── queries ──────────────────────────────────────────────────────────────

  const hotelQuery = useQuery({
    queryKey: ["hotel-affiliation-hotel", hotelId],
    enabled: !!hotelId,
    queryFn: async () => {
      const res = await axiosInstance.get(`/hotel/${hotelId}`);
      return res.data;
    },
  });

  useEffect(() => {
    const hotel = hotelQuery.data;
    if (!hotel) return;
    setSettingsForm({
      allowAgentAffiliation:       !!hotel.allowAgentAffiliation,
      autoApproveAgents:           !!hotel.autoApproveAgents,
      defaultCommissionPercentage: hotel.defaultCommissionPercentage ?? null,
    });
    setActivationSlaForm((p) => ({
      ...p,
      defaultBaseCommissionRate: hotel.defaultCommissionPercentage ?? 10,
    }));
  }, [hotelQuery.data]);

  const isAffiliationEnabled = hotelQuery.data?.allowAgentAffiliation === true;

  const applicationsQuery = useQuery({
    queryKey: ["hotel-affiliation-applications", hotelId],
    enabled: !!hotelId && isAffiliationEnabled,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(`/HotelAgentApplications/hotels/${hotelId}/applications`);
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const applicationDetailQuery = useQuery({
    queryKey: ["hotel-affiliation-application", hotelId, selectedApplicationId],
    enabled: !!hotelId && !!selectedApplicationId && drawerOpen && isAffiliationEnabled,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedApplicationId}`
      );
      return res.data;
    },
  });

  const slaDefaultsQuery = useQuery({
    queryKey: ["hotel-sla-defaults", hotelId],
    enabled: !!hotelId && isAffiliationEnabled,
    retry: false,
    queryFn: async () => {
      const r = await axiosInstance.get(`/agent-sla/hotel/${hotelId}/defaults`);
      return r.data;
    },
  });

 const refreshAll = async () => {
  await Promise.all([
    queryClient.refetchQueries({ queryKey: ["hotel-affiliation-hotel", hotelId] }),
    queryClient.refetchQueries({ queryKey: ["hotel-affiliation-applications", hotelId] }),
    queryClient.refetchQueries({ queryKey: ["hotel-sla-defaults", hotelId] }),
    selectedApplicationId
      ? queryClient.refetchQueries({
          queryKey: ["hotel-affiliation-application", hotelId, selectedApplicationId],
        })
      : Promise.resolve(),
  ]);
};
  // ── mutations ─────────────────────────────────────────────────────────────

  const saveSettingsMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axiosInstance.put(
        `/HotelAgentApplications/hotels/${hotelId}/settings`,
        payload
      );
      return res.data;
    },
    onSuccess: async (_, payload) => {
      notify.success("Affiliation settings updated.");
      await queryClient.invalidateQueries({ queryKey: ["hotel-affiliation-hotel", hotelId], exact: true });
      await hotelQuery.refetch();
      if (payload.allowAgentAffiliation) {
        await queryClient.invalidateQueries({ queryKey: ["hotel-affiliation-applications", hotelId] });
      }
      setTimeout(() => {
        const currentHotel = queryClient.getQueryData(["hotel-affiliation-hotel", hotelId]);
        if (currentHotel?.allowAgentAffiliation !== payload.allowAgentAffiliation) {
          window.location.reload();
        }
      }, 500);
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Failed to update settings.");
    },
  });

  const saveSlaMutation = useMutation({
    mutationFn: async (payload) => {
      const r = await axiosInstance.put(`/agent-sla/hotel/${hotelId}/defaults`, payload);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hotel-sla-defaults", hotelId] });
    },
  });

  // Combined: enable affiliation + optionally save SLA policy
  const activateMutation = useMutation({
    mutationFn: async () => {
      // 1. Enable affiliation settings
      await axiosInstance.put(`/HotelAgentApplications/hotels/${hotelId}/settings`, {
        ...settingsForm,
        allowAgentAffiliation: true,
        defaultCommissionPercentage: activationSlaForm.defaultBaseCommissionRate,
      });
      // 2. Save SLA policy (even if minimal — so the hotel has a record)
      if (!skipSla) {
        await axiosInstance.put(`/agent-sla/hotel/${hotelId}/defaults`, {
          defaultBaseCommissionRate:     activationSlaForm.defaultBaseCommissionRate,
          defaultMinBookingsPerMonth:    activationSlaForm.defaultMinBookingsPerMonth,
          defaultMaxCancellationRate:    activationSlaForm.defaultMaxCancellationRate,
          defaultMaxResponseTimeMinutes: 120,
          defaultMinGuestRating:         3.5,
          reviewPeriodDays:              activationSlaForm.reviewPeriodDays,
          slaText:                       activationSlaForm.slaText,
          useCustomSlaPerAgent:          false,
        });
      }
    },
    onSuccess: async () => {
      notify.success("Affiliation activated" + (!skipSla ? " with SLA policy." : "."));
      await refreshAll();
      setTimeout(() => window.location.reload(), 600);
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Activation failed.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axiosInstance.post(
        `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedApplicationId}/approve`,
        payload
      );
      return res.data;
    },
    onSuccess: async () => {
      notify.success("Agent approved successfully!");
      closeDrawer();
      setActiveTab("overview");
      await refreshAll();
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Approval failed.");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axiosInstance.post(
        `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedApplicationId}/reject`,
        payload
      );
      return res.data;
    },
    onSuccess: async () => {
      notify.success("Application rejected.");
      closeDrawer();
      await refreshAll();
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Reject action failed.");
    },
  });
 
  const resubmitMutation = useMutation({
      mutationFn: async (payload) => {
        const res = await axiosInstance.post(
          `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedApplicationId}/request-resubmission`,
          payload
        );
        return res.data;
      },
      onSuccess: async () => {
        notify.success("Resubmission requested.");
        await refreshAll();   // refresh FIRST — activeAgent recalculates from fresh data
        closeDrawer();        // THEN close
      },
      onError: (err) => {
        notify.error(err?.response?.data?.message || "Resubmission request failed.");
      },
    });

  const clearActiveAgentMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(`/HotelAgentApplications/hotels/${hotelId}/clear-active-agent`);
      return res.data;
    },
    onSuccess: async () => {
      notify.success("Active agent cleared.");
      setClearAgentModalOpen(false);
      await refreshAll();
    },
    onError: (err) => {
      notify.error(err?.response?.data?.message || "Failed to clear active agent.");
    },
  });

  const handleDisableAffiliation = () => {
    saveSettingsMutation.mutate({ ...settingsForm, allowAgentAffiliation: false });
    setDisableModalOpen(false);
  };

  // ── derived state ─────────────────────────────────────────────────────────

  const hotel        = hotelQuery.data;
  const applications = useMemo(() => applicationsQuery.data ?? [], [applicationsQuery.data]);
  const isLoading    = hotelQuery.isLoading;

  const summary = useMemo(() => {
    const pending  = applications.filter((x) => { const s = String(x.status ?? "").toLowerCase(); return s.includes("pending") || s === "1" || s === "2"; }).length;
    const approved = applications.filter((x) => { const s = String(x.status ?? "").toLowerCase(); return s.includes("approved") || s === "3"; }).length;
    const rejected = applications.filter((x) => { const s = String(x.status ?? "").toLowerCase(); return s.includes("rejected") || s === "4"; }).length;
    return { pending, approved, rejected, total: applications.length };
  }, [applications]);

const activeAgent = useMemo(() => {
  const activeProfileId = hotel?.activeAgentProfileId ?? hotel?.ActiveAgentProfileId;
  if (!activeProfileId) return null;
  return applications.find((x) => x.agentProfileId === activeProfileId) ?? null;
}, [applications, hotel]);

  const openApplication = (applicationId) => {
    setSelectedApplicationId(applicationId);
    setReviewNotes("");
    setCommissionPercentage(hotel?.defaultCommissionPercentage ?? null);
    setDrawerOpen(true);
  };

  // ── early returns ─────────────────────────────────────────────────────────

  if (!hotelId) return <Alert type="warning" title="No hotel context." showIcon style={{ margin: 24 }} />;
  if (isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spin size="large" /></div>;
  if (hotelQuery.isError) return <Alert type="error" title="Failed to load hotel settings." showIcon style={{ margin: 24 }} />;

  // ══ NOT ACTIVATED ═════════════════════════════════════════════════════════

  if (!isAffiliationEnabled) {
    return (
      <div className="hms-page af-page">
        <Space direction="vertical" size={20} style={{ width: "100%" }}>

          <div>
            <Title level={3} style={{ marginBottom: 4 }}>Hotel Affiliation Management</Title>
            <Text type="secondary">Partner with travel agents to expand your hotel's booking reach.</Text>
          </div>

          {/* ── Wizard card ── */}
          <Card style={{ borderRadius: 12, border: "1px solid var(--hms-accent)", background: "linear-gradient(135deg,var(--hms-wash) 0%,var(--hms-wash) 100%)" }}>
            <Steps
              current={activationStep}
              style={{ marginBottom: 28 }}
              items={[
                { title: "Affiliation Settings", description: "Commission & auto-approve" },
                { title: "SLA Policy",           description: "Targets & commission" },
                { title: "Activate",             description: "Go live" },
              ]}
            />

            {/* ── Step 0: Settings ── */}
            {activationStep === 0 && (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Alert type="info" showIcon icon={<InfoCircleOutlined />}
                  message="Set your default commission rate and decide whether applications are approved manually or automatically." />
                <Row gutter={[20, 16]}>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "14px 16px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong>Auto-Approve Agents</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>Skip manual review for new applications</Text>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={settingsForm.autoApproveAgents}
                          onChange={(v) => setSettingsForm((p) => ({ ...p, autoApproveAgents: v }))}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "14px 16px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong>Default Commission %</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>Applied to new agent approvals</Text>
                      <InputNumber
                        min={0} max={100} addonAfter="%" style={{ width: "100%", marginTop: 8 }}
                        value={settingsForm.defaultCommissionPercentage}
                        onChange={(v) => {
                          setSettingsForm((p) => ({ ...p, defaultCommissionPercentage: v }));
                          setActivationSlaForm((p) => ({ ...p, defaultBaseCommissionRate: v ?? 10 }));
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button type="primary" onClick={() => setActivationStep(1)} icon={<ArrowRightOutlined />}>
                    Next: Configure SLA
                  </Button>
                </div>
              </Space>
            )}

            {/* ── Step 1: SLA Policy ── */}
            {activationStep === 1 && (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Alert
                  type="info" showIcon icon={<InfoCircleOutlined />}
                  message="Agents must read and accept these terms before submitting an application."
                  description={
                    <span>
                      You can skip this now and configure it later in the{" "}
                      <strong>SLA Management</strong> page, but agents won't see any
                      terms until a policy is saved.
                    </span>
                  }
                />

                {/* Minimal policy fields for activation wizard */}
                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "12px 14px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13 }}>Base Commission Rate</Text>
                      <InputNumber
                        min={0} max={100} addonAfter="%" style={{ width: "100%", marginTop: 6 }}
                        value={activationSlaForm.defaultBaseCommissionRate}
                        onChange={(v) => setActivationSlaForm((p) => ({ ...p, defaultBaseCommissionRate: v }))}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "12px 14px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13 }}>Min Bookings / Month</Text>
                      <InputNumber
                        min={0} addonAfter="bookings" style={{ width: "100%", marginTop: 6 }}
                        value={activationSlaForm.defaultMinBookingsPerMonth}
                        onChange={(v) => setActivationSlaForm((p) => ({ ...p, defaultMinBookingsPerMonth: v }))}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "12px 14px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13 }}>Max Cancellation Rate</Text>
                      <InputNumber
                        min={0} max={100} addonAfter="%" style={{ width: "100%", marginTop: 6 }}
                        value={activationSlaForm.defaultMaxCancellationRate}
                        onChange={(v) => setActivationSlaForm((p) => ({ ...p, defaultMaxCancellationRate: v }))}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: "12px 14px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13 }}>Review Period</Text>
                      <InputNumber
                        min={7} addonAfter="days" style={{ width: "100%", marginTop: 6 }}
                        value={activationSlaForm.reviewPeriodDays}
                        onChange={(v) => setActivationSlaForm((p) => ({ ...p, reviewPeriodDays: v }))}
                      />
                    </div>
                  </Col>
                  <Col xs={24}>
                    <div style={{ padding: "12px 14px", background: "#fff", borderRadius: 10, border: "1px solid #f0f0f0" }}>
                      <Text strong style={{ fontSize: 13 }}>SLA Policy Text (optional)</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Shown to agents verbatim. You can complete this later on the SLA page.
                      </Text>
                      <TextArea
                        rows={4} style={{ marginTop: 6 }}
                        value={activationSlaForm.slaText}
                        onChange={(e) => setActivationSlaForm((p) => ({ ...p, slaText: e.target.value }))}
                        placeholder="Describe termination conditions, confidentiality, and other important terms…"
                      />
                    </div>
                  </Col>
                </Row>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Button onClick={() => setActivationStep(0)}>Back</Button>
                  <Space>
                    <Button
                      onClick={() => { setSkipSla(true); setActivationStep(2); }}
                    >
                      Skip SLA for now
                    </Button>
                    <Button type="primary" onClick={() => { setSkipSla(false); setActivationStep(2); }} icon={<ArrowRightOutlined />}>
                      Next: Review & Activate
                    </Button>
                  </Space>
                </div>
              </Space>
            )}

            {/* ── Step 2: Confirm & Activate ── */}
            {activationStep === 2 && (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Auto-Approve</Text>
                      <div style={{ fontWeight: 600, marginTop: 2 }}>
                        {settingsForm.autoApproveAgents ? "Enabled" : "Disabled"}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Default Commission</Text>
                      <div style={{ fontWeight: 600, marginTop: 2 }}>
                        {settingsForm.defaultCommissionPercentage ?? activationSlaForm.defaultBaseCommissionRate}%
                      </div>
                    </Card>
                  </Col>
                  {!skipSla && (
                    <>
                      <Col xs={24} sm={12}>
                        <Card size="small" style={{ borderRadius: 10, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Base Commission (SLA)</Text>
                          <div style={{ fontWeight: 600, color: "#52c41a", marginTop: 2 }}>
                            {activationSlaForm.defaultBaseCommissionRate}%
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Card size="small" style={{ borderRadius: 10, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Review Period</Text>
                          <div style={{ fontWeight: 600, color: "#52c41a", marginTop: 2 }}>
                            Every {activationSlaForm.reviewPeriodDays} days
                          </div>
                        </Card>
                      </Col>
                    </>
                  )}
                  {skipSla && (
                    <Col xs={24}>
                      <Alert type="warning" showIcon icon={<WarningOutlined />}
                        message="SLA policy not configured. Agents will not see any terms when applying. Configure it later from the SLA Management page."
                      />
                    </Col>
                  )}
                </Row>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Button onClick={() => setActivationStep(1)}>Back</Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    loading={activateMutation.isPending}
                    onClick={() => activateMutation.mutate()}
                    style={{ paddingInline: 32 }}
                  >
                    Activate Affiliation
                  </Button>
                </div>
              </Space>
            )}
          </Card>

          {/* Feature highlights */}
          <Row gutter={[16, 16]}>
            {[
              { icon: <TeamOutlined style={{ fontSize: 22, color: "var(--hms-accent)" }} />, title: "Agent Applications",    desc: "Review and approve travel agents who want to partner with your hotel." },
              { icon: <IdcardOutlined style={{ fontSize: 22, color: "#52c41a" }} />, title: "KYC Verification",   desc: "Agents go through identity & business verification before approval." },
              { icon: <DollarOutlined style={{ fontSize: 22, color: "#722ed1" }} />, title: "Commission Control", desc: "Set default commission rates and override them per agent." },
              { icon: <SafetyOutlined style={{ fontSize: 22, color: "#fa8c16" }} />, title: "SLA Enforcement",    desc: "Define performance targets agents must meet to maintain affiliation." },
            ].map((f) => (
              <Col xs={24} sm={12} lg={6} key={f.title}>
                <Card bordered style={{ borderRadius: 10, height: "100%" }} bodyStyle={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ flexShrink: 0, marginTop: 2 }}>{f.icon}</div>
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>{f.title}</Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>{f.desc}</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Space>
      </div>
    );
  }

  // ══ DETAIL DRAWER ═════════════════════════════════════════════════════════

  const detail   = applicationDetailQuery.data;
  const appStatus = String(detail?.status ?? "").toLowerCase();
  const isPending  = appStatus.includes("pending") || appStatus === "1" || appStatus === "2";
  const isApproved = appStatus.includes("approved") || appStatus === "3";
  const isRejected = appStatus.includes("rejected") || appStatus === "4";

  const chipOf = (v) => {
    const s = String(v ?? "").toLowerCase();
    if (s.includes("approved") || s === "3") return "available";
    if (s.includes("rejected") || s === "4") return "cancelled";
    if (s.includes("pending") || s === "1" || s === "2") return "pending";
    if (s.includes("suspend")) return "noshow";
    return "";
  };
  const chipLabel = (v) => {
    const s = String(v ?? "").toLowerCase();
    if (s.includes("approved") || s === "3") return "Approved";
    if (s.includes("rejected") || s === "4") return "Rejected";
    if (s.includes("pending") || s === "1" || s === "2") return "Pending";
    return String(v ?? "—");
  };
  const kycChipOf = (v) => {
    const s = String(v ?? "").toLowerCase();
    if (s.includes("approved") || s === "4") return "available";
    if (s.includes("rejected") || s === "5") return "cancelled";
    if (s.includes("resubmission") || s === "6") return "noshow";
    if (s.includes("review") || s === "3" || s.includes("submitted") || s === "2") return "confirmed";
    return "";
  };
  const initialsOf = (name = "") =>
    String(name).split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase() || "A";

  const applicationColumns = [
    {
      title: "Agent",
      dataIndex: "agentName",
      key: "agentName",
      render: (_, row) => (
        <span className="hms-person">
          <span className="hms-avatar">{initialsOf(row.agentName)}</span>
          <span className="hms-cell-stack">
            <span className="hms-cell-main">{row.agentName || "Unknown agent"}</span>
            <span className="hms-cell-sub">{row.companyName || "—"}</span>
          </span>
        </span>
      ),
    },
    { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber", render: (v) => v || <span className="hms-muted">—</span> },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (v) => <StatusTag status={chipOf(v)}>{chipLabel(v)}</StatusTag>,
    },
    {
      title: "KYC",
      key: "kyc",
      width: 170,
      render: (_, row) => {
        const kyc = row.latestKycStatus ?? row.latestKyc?.reviewStatus ?? "NotStarted";
        return <StatusTag status={kycChipOf(kyc)}>{kycLabel(kyc)}</StatusTag>;
      },
    },
    { title: "Applied", dataIndex: "appliedAtUtc", key: "appliedAtUtc", width: 140, render: (v) => fmtDay(v) },
    {
      title: "",
      key: "actions",
      align: "right",
      width: 130,
      render: (_, row) => (
        <RowActions
          actions={[
            { key: "review", label: "Review application", icon: "view", showLabel: true, onClick: () => openApplication(row.hotelAgentApplicationId) },
          ]}
        />
      ),
    },
  ];

  // ══ MAIN ENABLED VIEW ═════════════════════════════════════════════════════

  const hasSlaPolicy = !!slaDefaultsQuery.data;

  return (
    <div className="hms-page af-page">
      <PageHeader
        actions={
          <>
            <StatusTag status="available">Affiliation active</StatusTag>
            <Button icon={<ReloadOutlined />} onClick={refreshAll}>
              Refresh
            </Button>
            {activeAgent && <Button onClick={() => setClearAgentModalOpen(true)}>Clear active agent</Button>}
            <Button danger onClick={() => setDisableModalOpen(true)}>
              Disable affiliation
            </Button>
          </>
        }
      />
      <Space direction="vertical" size={16} style={{ width: "100%" }}>

        {/* SLA policy missing banner */}
        {!hasSlaPolicy && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message="No SLA policy configured"
            description="Agents won't see any SLA terms when applying until you create a policy. Set it up now to ensure agents understand their obligations."
            action={
              <Button type="primary" size="small" onClick={() => setActiveTab("sla")}>
                Configure SLA <ArrowRightOutlined />
              </Button>
            }
          />
        )}

        {/* Error banner */}
        {applicationsQuery.isError && (
          <Alert type="warning" showIcon title="Applications could not be loaded."
            description={applicationsQuery.error?.response?.data?.message || "You may not have permission to view these."} />
        )}

        {/* Pending attention banner */}
        {summary.pending > 0 && (
          <Alert type="info" showIcon icon={<ClockCircleOutlined />}
            message={<Text><Text strong>{summary.pending}</Text> agent application{summary.pending !== 1 ? "s" : ""} awaiting your review.</Text>}
            action={<Button size="small" onClick={() => setActiveTab("applications")}>Review Now <ArrowRightOutlined /></Button>}
          />
        )}

        <SectionCard flush>
          <StatStrip
            items={[
              { label: "Active agent", value: activeAgent ? activeAgent.agentName || "Active" : "None" },
              { label: "Pending review", value: summary.pending, tone: summary.pending ? "warn" : undefined },
              { label: "Approved", value: summary.approved, tone: "good" },
              { label: "Rejected", value: summary.rejected, tone: summary.rejected ? "bad" : undefined },
            ]}
          />
        </SectionCard>

        {/* Tabs */}
        <TabsHost
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[

            // ── OVERVIEW ────────────────────────────────────────────────────
            {
              key: "overview",
              label: "Overview",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card
                      title={<Space><UserOutlined />Active Agent</Space>}
                      extra={activeAgent && <Tag color="green" icon={<CheckCircleOutlined />}>Approved</Tag>}
                      style={{ borderRadius: 10, height: "100%" }}
                    >
                      {activeAgent ? (
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                          <Space align="start" size={16}>
                            <Avatar size={64} icon={<UserOutlined />} style={{ background: "var(--hms-accent)", flexShrink: 0 }} />
                            <div>
                              <Title level={4} style={{ marginBottom: 2 }}>{activeAgent.agentName || "—"}</Title>
                              <Text type="secondary">{activeAgent.companyName || "—"}</Text>
                              <br />
                              <Space style={{ marginTop: 6 }}>
                                <PhoneOutlined style={{ color: "#8c8c8c" }} />
                                <Text>{activeAgent.phoneNumber || "—"}</Text>
                              </Space>
                            </div>
                          </Space>
                          <Divider style={{ margin: "4px 0" }} />
                          <Row gutter={[16, 8]}>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>Commission Rate</Text>
                              <div style={{ fontWeight: 600, fontSize: 18 }}>
                                {hotel?.defaultCommissionPercentage != null ? `${hotel.defaultCommissionPercentage}%` : "—"}
                              </div>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>Approved On</Text>
                              <div style={{ fontWeight: 500 }}>{fmtDate(activeAgent.reviewedAtUtc)}</div>
                            </Col>
                          </Row>
                        </Space>
                      ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                          <Space direction="vertical">
                            <Text type="secondary">No active agent assigned yet.</Text>
                            {summary.pending > 0 && (
                              <Button type="primary" size="small" onClick={() => setActiveTab("applications")}>
                                Review pending applications
                              </Button>
                            )}
                          </Space>
                        } />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card title={<Space><SafetyOutlined />Affiliation Settings</Space>} style={{ borderRadius: 10, height: "100%" }}>
                      <Space direction="vertical" size={16} style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                          <div>
                            <Text strong>Auto-Approve Agents</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>Skip manual review</Text>
                          </div>
                          <Switch checked={settingsForm.autoApproveAgents} onChange={(v) => setSettingsForm((p) => ({ ...p, autoApproveAgents: v }))} />
                        </div>
                        <div style={{ padding: "12px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                          <Text strong>Default Commission %</Text>
                          <InputNumber min={0} max={100} style={{ width: "100%", marginTop: 8 }} addonAfter="%" value={settingsForm.defaultCommissionPercentage}
                            onChange={(v) => setSettingsForm((p) => ({ ...p, defaultCommissionPercentage: v }))} />
                        </div>
                        <Button type="primary" block loading={saveSettingsMutation.isPending} onClick={() => saveSettingsMutation.mutate(settingsForm)}>
                          Save Settings
                        </Button>
                      </Space>
                    </Card>
                  </Col>
                  {summary.total > 0 && (
                    <Col xs={24}>
                      <Card title="Application Pipeline" style={{ borderRadius: 10 }} extra={<Button type="link" onClick={() => setActiveTab("applications")}>View All</Button>}>
                        <Row gutter={[16, 8]} align="middle">
                          <Col xs={24} sm={8}>
                            <Text type="secondary">Total Applications</Text>
                            <div style={{ fontSize: 24, fontWeight: 700 }}>{summary.total}</div>
                          </Col>
                          <Col xs={24} sm={16}>
                            <Space direction="vertical" style={{ width: "100%" }} size={4}>
                              {summary.approved > 0 && (<div><Text type="secondary" style={{ fontSize: 12 }}>Approved ({summary.approved})</Text><Progress percent={Math.round((summary.approved / summary.total) * 100)} strokeColor="#52c41a" showInfo={false} size="small" /></div>)}
                              {summary.pending  > 0 && (<div><Text type="secondary" style={{ fontSize: 12 }}>Pending ({summary.pending})</Text><Progress percent={Math.round((summary.pending / summary.total) * 100)} strokeColor="#faad14" showInfo={false} size="small" /></div>)}
                              {summary.rejected > 0 && (<div><Text type="secondary" style={{ fontSize: 12 }}>Rejected ({summary.rejected})</Text><Progress percent={Math.round((summary.rejected / summary.total) * 100)} strokeColor="#ff4d4f" showInfo={false} size="small" /></div>)}
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  )}
                </Row>
              ),
            },

            // ── APPLICATIONS ───────────────────────────────────────────────
            {
              key: "applications",
              label: "Applications",
              count: summary.pending > 0 ? summary.pending : undefined,
              children: (
                <SectionCard flush title={<CardTitle count={applications.length}>Agent applications</CardTitle>}>
                  <Table
                    className="hms-table"
                    rowKey="hotelAgentApplicationId"
                    columns={applicationColumns}
                    dataSource={applications}
                    pagination={tablePagination("application", { hideOnSinglePage: true })}
                    loading={applicationsQuery.isLoading}
                    onRow={(row) => ({ onClick: () => openApplication(row.hotelAgentApplicationId), style: { cursor: "pointer" } })}
                    locale={{ emptyText: <div className="hms-empty">No applications yet.</div> }}
                  />
                </SectionCard>
              ),
            },

            // ── SLA ────────────────────────────────────────────────────────
            {
              key: "sla",
              label: hasSlaPolicy ? "SLA" : "SLA (not set)",
              children: (
                <Card style={{ borderRadius: 10 }}>
                  {slaDefaultsQuery.isLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>
                  ) : (
                    <Space direction="vertical" size={16} style={{ width: "100%" }}>
                      {!hasSlaPolicy ? (
                        <Alert type="warning" showIcon icon={<WarningOutlined />}
                          message="No SLA policy configured yet"
                          description="Agents applying to your hotel won't see any SLA terms until you create one. A well-defined SLA protects both parties."
                        />
                      ) : (
                        <Alert type="success" showIcon icon={<CheckCircleOutlined />}
                          message="SLA policy is active"
                          description={`Base commission: ${slaDefaultsQuery.data.defaultBaseCommissionRate}% · Review cycle: every ${slaDefaultsQuery.data.reviewPeriodDays} days`}
                        />
                      )}

                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Full SLA configuration — policy text, performance targets, commission bonuses, and agent agreements — is managed on the dedicated SLA page.
                        </Text>
                        <Button
                          type="primary"
                          icon={<FileTextOutlined />}
                          onClick={() => navigate("/hotels/hotel-sla")}
                        >
                          Open SLA Management
                        </Button>
                      </div>

                      {/* Quick snapshot */}
                      {hasSlaPolicy && (
                        <Row gutter={[12, 12]}>
                          {[
                            { label: "Base commission",     value: `${slaDefaultsQuery.data.defaultBaseCommissionRate}%` },
                            { label: "Min bookings / mo",   value: `${slaDefaultsQuery.data.defaultMinBookingsPerMonth}` },
                            { label: "Max cancellation",    value: `${slaDefaultsQuery.data.defaultMaxCancellationRate}%` },
                            { label: "Review cycle",        value: `${slaDefaultsQuery.data.reviewPeriodDays} days` },
                          ].map((s) => (
                            <Col xs={12} sm={6} key={s.label}>
                              <Card size="small" style={{ borderRadius: 8, textAlign: "center" }}>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--hms-accent)" }}>{s.value}</div>
                                <Text type="secondary" style={{ fontSize: 11 }}>{s.label}</Text>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Space>
                  )}
                </Card>
              ),
            },

            // ── ACTIVITY ───────────────────────────────────────────────────
            {
              key: "activity",
              label: "Activity",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card title="Recent Application Activity" style={{ borderRadius: 10 }}>
                      {applications.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity yet." />
                      ) : (
                        <Timeline items={applications.slice().sort((a, b) => new Date(b.reviewedAtUtc || b.appliedAtUtc) - new Date(a.reviewedAtUtc || a.appliedAtUtc)).slice(0, 10).map((app) => {
                          const s = String(app.status ?? "").toLowerCase();
                          const isApp = s.includes("approved") || s === "3";
                          const isRej = s.includes("rejected") || s === "4";
                          return {
                            color: isApp ? "green" : isRej ? "red" : "blue",
                            dot: isApp ? <CheckCircleOutlined /> : isRej ? <CloseCircleOutlined /> : <ClockCircleOutlined />,
                            children: (
                              <div>
                                <Text strong>{app.agentName || "Unknown Agent"}</Text>
                                {app.companyName && <Text type="secondary"> · {app.companyName}</Text>}
                                <br />
                                <Tag color={statusTagColor(app.status)} style={{ marginTop: 4 }}>{String(app.status)}</Tag>
                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{fmt(app.reviewedAtUtc || app.appliedAtUtc)}</Text>
                              </div>
                            ),
                          };
                        })} />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card title="Quick Stats" style={{ borderRadius: 10 }}>
                      <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        {[
                          { label: "Approval Rate",     value: summary.total > 0 ? `${Math.round((summary.approved / summary.total) * 100)}%` : "—", color: "#52c41a" },
                          { label: "Pending Rate",      value: summary.total > 0 ? `${Math.round((summary.pending / summary.total) * 100)}%` : "—",  color: "#faad14" },
                          { label: "Total Applications",value: summary.total, color: "var(--hms-accent)" },
                          { label: "Commission Rate",   value: hotel?.defaultCommissionPercentage != null ? `${hotel.defaultCommissionPercentage}%` : "Not set", color: "#722ed1" },
                          { label: "SLA Policy",        value: hasSlaPolicy ? "Configured" : "Not set", color: hasSlaPolicy ? "#52c41a" : "#ff4d4f" },
                        ].map((stat) => (
                          <div key={stat.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#fafafa", borderRadius: 8, border: "1px solid #f0f0f0" }}>
                            <Text type="secondary">{stat.label}</Text>
                            <Text strong style={{ color: stat.color, fontSize: 16 }}>{stat.value}</Text>
                          </div>
                        ))}
                      </Space>
                    </Card>
                  </Col>
                </Row>
              ),
            },
          ]}
        />

        {/* ── Application detail drawer ── */}
        <Drawer
          title="Application review"
          size={640}
          open={drawerOpen}
          onClose={closeDrawer}
          destroyOnHidden
          footer={
            detail && isPending ? (
              <div className="hms-form-actions" style={{ marginTop: 0 }}>
                <Button loading={resubmitMutation.isPending} onClick={() => resubmitMutation.mutate({ reviewNotes })}>
                  Request resubmission
                </Button>
                <Button danger loading={rejectMutation.isPending} onClick={() => rejectMutation.mutate({ reviewNotes })}>
                  Reject
                </Button>
                <Button
                  type="primary"
                  loading={approveMutation.isPending}
                  disabled={!!activeAgent && activeAgent.hotelAgentApplicationId !== selectedApplicationId}
                  title={!!activeAgent && activeAgent.hotelAgentApplicationId !== selectedApplicationId ? "Clear the active agent first" : undefined}
                  onClick={() => approveMutation.mutate({ reviewNotes, commissionPercentage })}
                >
                  Approve agent
                </Button>
              </div>
            ) : null
          }
        >
          {applicationDetailQuery.isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : detail ? (
            <>
              <div className="hms-summary">
                <div>
                  <div className="hms-summary-title">{detail.agent?.displayName || "—"}</div>
                  <div className="hms-summary-sub">
                    {detail.agent?.companyName || "—"} · applied {fmtDay(detail.appliedAtUtc)}
                  </div>
                </div>
                <StatusTag status={chipOf(detail.status)}>{chipLabel(detail.status)}</StatusTag>
              </div>

              <DetailList
                items={[
                  { label: "Phone", value: detail.agent?.phoneNumber },
                  { label: "Business address", value: detail.agent?.businessAddress },
                  { label: "License number", value: detail.agent?.licenseNumber },
                  {
                    label: "Verified",
                    value: (
                      <StatusTag status={detail.agent?.isVerified ? "available" : ""}>
                        {detail.agent?.isVerified ? "Verified" : "Not verified"}
                      </StatusTag>
                    ),
                  },
                ]}
              />

              <Section title="KYC submissions">
                {detail.kycSubmissions?.length ? (
                  detail.kycSubmissions.map((kyc, idx) => (
                    <div key={kyc.agentKycSubmissionId} style={{ marginBottom: 16 }}>
                      <div className="hms-summary" style={{ marginBottom: 4 }}>
                        <div className="hms-cell-main">{idx === 0 ? "Latest KYC" : `KYC #${kyc.agentKycSubmissionId}`}</div>
                        <StatusTag status={kycChipOf(kyc.reviewStatus)}>{kycLabel(kyc.reviewStatus)}</StatusTag>
                      </div>
                      <DetailList
                        items={[
                          { label: "Identity number", value: kyc.identityNumber },
                          { label: "Business reg. no.", value: kyc.businessRegistrationNumber },
                          { label: "Tax number", value: kyc.taxNumber },
                          { label: "Submitted", value: fmt(kyc.submittedAtUtc) },
                          kyc.documents?.length > 0
                            ? {
                                label: "Documents",
                                value: (
                                  <span>
                                    {kyc.documents.map((doc) => (
                                      <a key={doc.agentKycDocumentId} href={doc.filePath} target="_blank" rel="noreferrer" style={{ marginRight: 12 }}>
                                        {doc.documentType}
                                      </a>
                                    ))}
                                  </span>
                                ),
                              }
                            : null,
                        ]}
                      />
                    </div>
                  ))
                ) : (
                  <div className="hms-empty">No KYC submissions.</div>
                )}
              </Section>

              {detail.reviewedAtUtc && (
                <Section title="Review history">
                  <DetailList
                    items={[
                      { label: "Last reviewed", value: fmt(detail.reviewedAtUtc) },
                      detail.reviewNotes ? { label: "Notes", value: detail.reviewNotes } : null,
                    ]}
                  />
                </Section>
              )}

              {(isPending || isApproved || isRejected) && (
                <Section title={isPending ? "Your decision" : "Decision"}>
                  {isApproved && <Alert type="success" showIcon title="This application has been approved." description={detail.reviewNotes || undefined} />}
                  {isRejected && <Alert type="error" showIcon title="This application was rejected." description={detail.reviewNotes || undefined} />}
                  {isPending && (
                    <>
                      {!!activeAgent && activeAgent.hotelAgentApplicationId !== selectedApplicationId && (
                        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title="The hotel already has an active agent. Clear them before approving a new one." />
                      )}
                      <div className="hms-field">
                        <label htmlFor="af-commission">
                          Commission %{" "}
                          <Text type="secondary" style={{ fontWeight: 400 }}>
                            (default {hotel?.defaultCommissionPercentage ?? "—"}%)
                          </Text>
                        </label>
                        <InputNumber
                          id="af-commission"
                          min={0}
                          max={100}
                          addonAfter="%"
                          style={{ width: "100%" }}
                          value={commissionPercentage}
                          onChange={setCommissionPercentage}
                          placeholder={`Default: ${hotel?.defaultCommissionPercentage ?? "—"}%`}
                        />
                      </div>
                      <div className="hms-field">
                        <label htmlFor="af-notes">Review notes</label>
                        <TextArea id="af-notes" rows={3} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Optional" />
                      </div>
                    </>
                  )}
                </Section>
              )}
            </>
          ) : (
            <div className="hms-empty">No application selected.</div>
          )}
        </Drawer>

        {/* Modals */}
        <Modal title="Disable agent affiliation?"
          open={disableModalOpen} onOk={handleDisableAffiliation} onCancel={() => setDisableModalOpen(false)}
          okText="Disable" cancelText="Cancel" okButtonProps={{ danger: true, loading: saveSettingsMutation.isPending }}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text>Are you sure you want to disable agent affiliation for this hotel?</Text>
            <Alert type="warning" showIcon title="This will prevent new agent applications and may affect existing agent bookings." />
          </Space>
        </Modal>

        <Modal title="Clear active agent?"
          open={clearAgentModalOpen} onOk={() => clearActiveAgentMutation.mutate()} onCancel={() => setClearAgentModalOpen(false)}
          okText="Clear agent" cancelText="Cancel" okButtonProps={{ loading: clearActiveAgentMutation.isPending }}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text>This will remove <Text strong>{activeAgent?.agentName || "the current agent"}</Text> as the active agent.</Text>
            <Alert type="info" showIcon title="Their application record and KYC data will be preserved." />
          </Space>
        </Modal>
      </Space>
    </div>
  );
};

export default HotelAffiliationManagementPage;